// The five summary tiles above the Bodyweight trend chart (BodyweightTrendCard.js):
// current trend weight, bodyweight goal, pace, days logged, last weigh-in.
// Presentation-only — receives the result of buildBodyweightTrend
// (utils/nutrition-progress/bodyweightTrend.js), which already computed every
// number; this file only formats them and picks the wording.

import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { openAddGoal } from "../../../../../utils/modal-helpers";
import {
  RATE_SLOW_MAX_KG_PER_WEEK,
  RATE_STEADY_KG_PER_WEEK,
} from "../../../../../utils/nutrition-progress";
import { fmtUtc, toDisplayRate, toDisplayWeight } from "./viewTokens";

const bw = (t, key, opts) =>
  t(`nutritionPage.progress.bodyweightTrend.${key}`, opts);

const signed = (value, text) => (value > 0 ? `+${text}` : text);

const RATE_KEYS = {
  steady: "rateSteady",
  gainingSlowly: "rateGainingSlowly",
  gainingQuickly: "rateGainingQuickly",
  losingSlowly: "rateLosingSlowly",
  losingQuickly: "rateLosingQuickly",
};

const describeRate = (kgPerWeek) => {
  if (kgPerWeek === null) return null;
  const abs = Math.abs(kgPerWeek);
  if (abs < RATE_STEADY_KG_PER_WEEK) return RATE_KEYS.steady;
  const slow = abs <= RATE_SLOW_MAX_KG_PER_WEEK;
  if (kgPerWeek > 0) {
    return slow ? RATE_KEYS.gainingSlowly : RATE_KEYS.gainingQuickly;
  }
  return slow ? RATE_KEYS.losingSlowly : RATE_KEYS.losingQuickly;
};

const daysAgoLabel = (t, days) => {
  if (days === 0) return bw(t, "tiles.today");
  if (days === 1) return bw(t, "tiles.yesterday");
  return bw(t, "tiles.daysAgo", { count: days });
};

const Tile = ({ label, sub, subTone, tone, children }) => (
  <div
    className={
      tone ? `np-bwtrend__tile np-bwtrend__tile--${tone}` : "np-bwtrend__tile"
    }
  >
    <p className="np-bwtrend__tile-label">{label}</p>
    <p className="np-bwtrend__tile-value">{children}</p>
    {sub ? (
      <p
        className={
          subTone
            ? `np-bwtrend__tile-sub np-bwtrend__tile-sub--${subTone}`
            : "np-bwtrend__tile-sub"
        }
      >
        {sub}
      </p>
    ) : null}
  </div>
);

Tile.propTypes = {
  label: PropTypes.node,
  sub: PropTypes.node,
  subTone: PropTypes.string,
  tone: PropTypes.string,
  children: PropTypes.node,
};

const TrendWeightTile = ({ trend, isImperial, unit, quickRange }) => {
  const { t } = useTranslation("athlete-portal");
  const change =
    trend.changeOverRange === null
      ? null
      : toDisplayWeight(trend.changeOverRange, isImperial);
  const sub =
    change === null
      ? bw(t, "tiles.noChange")
      : bw(t, `tiles.changeOver.${quickRange}`, {
          value: signed(change, change.toFixed(1)),
          unit,
        });
  return (
    <Tile label={bw(t, "tiles.trendWeight")} sub={sub}>
      {toDisplayWeight(trend.currentTrend, isImperial).toFixed(1)}{" "}
      <span className="np-bwtrend__tile-unit">{unit}</span>
    </Tile>
  );
};

TrendWeightTile.propTypes = {
  trend: PropTypes.object.isRequired,
  isImperial: PropTypes.bool,
  unit: PropTypes.string,
  quickRange: PropTypes.string,
};

const EmptyGoalTile = () => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div className="np-bwtrend__tile np-bwtrend__tile--empty">
      <p className="np-bwtrend__tile-label">{bw(t, "tiles.goalNone")}</p>
      <button
        type="button"
        data-button-type="outline"
        className="np-bwtrend__set-goal"
        onClick={() => openAddGoal("bodyWeight")}
      >
        <span>{bw(t, "tiles.setGoal")}</span>
      </button>
    </div>
  );
};

// Why there is no rate to show: too old a last weigh-in, or too little history.
const noRateKey = (trend) =>
  trend.isProjectionStale ? "tiles.rateStale" : "tiles.rateNoData";

const RateTile = ({ trend, isImperial, unit }) => {
  const { t } = useTranslation("athlete-portal");
  const rateKey = describeRate(trend.rateKgPerWeek);
  const rate =
    trend.rateKgPerWeek === null
      ? null
      : toDisplayRate(trend.rateKgPerWeek, isImperial);
  return (
    <Tile
      label={bw(t, "tiles.rate")}
      sub={bw(t, rateKey ? `tiles.${rateKey}` : noRateKey(trend))}
    >
      {rate === null ? (
        "—"
      ) : (
        <>
          {signed(rate, rate.toFixed(2))}{" "}
          <span className="np-bwtrend__tile-unit">
            {bw(t, "tiles.perWeek", { unit })}
          </span>
        </>
      )}
    </Tile>
  );
};

RateTile.propTypes = {
  trend: PropTypes.object.isRequired,
  isImperial: PropTypes.bool,
  unit: PropTypes.string,
};

const GoalTile = ({ goal, isImperial, unit }) => {
  const { t } = useTranslation("athlete-portal");
  const toGo = toDisplayWeight(goal.toGo, isImperial);
  const direction = bw(t, `tiles.goalDirection.${goal.direction}`);
  return (
    <Tile
      tone="goal"
      subTone="good"
      label={`${bw(t, "tiles.goal")} · ${direction}`}
      sub={bw(t, "tiles.goalSub", {
        date: fmtUtc(goal.utcTargetDate),
        value: signed(toGo, toGo.toFixed(1)),
        unit,
      })}
    >
      {toDisplayWeight(goal.targetWeight, isImperial).toFixed(1)}{" "}
      <span className="np-bwtrend__tile-unit">{unit}</span>
    </Tile>
  );
};

GoalTile.propTypes = {
  goal: PropTypes.object.isRequired,
  isImperial: PropTypes.bool,
  unit: PropTypes.string,
};

const PACE_SUB_KEYS = {
  onPace: "paceOnPace",
  ahead: "paceAhead",
  behind: "paceBehind",
  offPace: "paceOff",
};

const paceTone = (paceState) => {
  if (paceState === null) return undefined;
  return paceState === "onPace" ? "good" : "warn";
};

// With a goal: the actual weekly rate next to the rate the goal needs, and
// where the current pace lands on the goal date.
const PaceTile = ({ trend, isImperial, unit }) => {
  const { t } = useTranslation("athlete-portal");
  const { goal, rateKgPerWeek } = trend;
  const rate =
    rateKgPerWeek === null ? null : toDisplayRate(rateKgPerWeek, isImperial);
  const required = toDisplayRate(goal.requiredRateKgPerWeek, isImperial);
  const subKey = PACE_SUB_KEYS[goal.paceState];
  const projected =
    goal.projectedWeight === null
      ? null
      : toDisplayWeight(goal.projectedWeight, isImperial);
  const rateText = rate === null ? "—" : signed(rate, rate.toFixed(2));
  return (
    <Tile
      label={bw(t, "tiles.pace")}
      subTone={paceTone(goal.paceState)}
      sub={
        subKey
          ? bw(t, `tiles.${subKey}`, { value: projected?.toFixed(1), unit })
          : bw(t, noRateKey(trend))
      }
    >
      {trend.isProjectionStale ? (
        "—"
      ) : (
        <>
          {rateText}{" "}
          <span className="np-bwtrend__tile-unit">
            / {signed(required, required.toFixed(2))}{" "}
            {bw(t, "tiles.perWeek", { unit })}
          </span>
        </>
      )}
    </Tile>
  );
};

PaceTile.propTypes = {
  trend: PropTypes.object.isRequired,
  isImperial: PropTypes.bool,
  unit: PropTypes.string,
};

const DaysLoggedTile = ({ trend }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <Tile
      label={bw(t, "tiles.daysLogged")}
      sub={
        trend.coveragePct === null
          ? null
          : bw(t, "tiles.coverage", { pct: trend.coveragePct })
      }
    >
      {trend.daysLogged}{" "}
      <span className="np-bwtrend__tile-unit">/ {trend.daysInRange}</span>
    </Tile>
  );
};

DaysLoggedTile.propTypes = { trend: PropTypes.object.isRequired };

const LastWeighInTile = ({ trend }) => {
  const { t } = useTranslation("athlete-portal");
  const date = fmtUtc(trend.lastWeighIn.utcDate);
  const stale = trend.isWeighInStale;
  return (
    <Tile
      label={bw(t, "tiles.lastWeighIn")}
      tone={stale ? "warn" : undefined}
      subTone={stale ? "warn" : undefined}
      sub={stale ? bw(t, "tiles.nudge", { date }) : date}
    >
      {daysAgoLabel(t, trend.daysSinceLastWeighIn)}
    </Tile>
  );
};

LastWeighInTile.propTypes = { trend: PropTypes.object.isRequired };

export const BodyweightTrendTiles = ({ trend, units, quickRange }) => {
  const isImperial = units === "imperial";
  const unit = isImperial ? "lb" : "kg";
  return (
    <div className="np-bwtrend__tiles">
      <TrendWeightTile
        trend={trend}
        isImperial={isImperial}
        unit={unit}
        quickRange={quickRange}
      />
      {trend.goal ? (
        <>
          <GoalTile goal={trend.goal} isImperial={isImperial} unit={unit} />
          <PaceTile trend={trend} isImperial={isImperial} unit={unit} />
        </>
      ) : (
        <>
          <EmptyGoalTile />
          <RateTile trend={trend} isImperial={isImperial} unit={unit} />
        </>
      )}
      <DaysLoggedTile trend={trend} />
      <LastWeighInTile trend={trend} />
    </div>
  );
};

BodyweightTrendTiles.propTypes = {
  trend: PropTypes.object.isRequired,
  units: PropTypes.string,
  quickRange: PropTypes.string,
};
